import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import Sidebar from '../components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';
import {
  Search,
  Plus,
  FileText,
  Download,
  Printer,
  Trash2,
  Edit,
  MoreVertical,
  Filter,
  ArrowUpDown,
  CheckCircle,
  Clock,
  XCircle,
  Send,
  FileEdit,
  Eye,
  ChevronLeft,
  ChevronRight,
  Settings,
  LogOut,
  Mail,
  Loader2,
} from 'lucide-react';
import { formatMoney } from '../utils/formatCurrency';
import { getTranslation } from '../i18n/translations';

const statusConfig = {
  draft: { labelKey: 'draft', color: 'bg-slate-500', textColor: 'text-slate-500', icon: FileEdit },
  pending: { labelKey: 'pending', color: 'bg-amber-500', textColor: 'text-amber-500', icon: Clock },
  sent: { labelKey: 'sent', color: 'bg-blue-500', textColor: 'text-blue-500', icon: Send },
  approved: { labelKey: 'statusApproved', color: 'bg-emerald-500', textColor: 'text-emerald-500', icon: CheckCircle },
  rejected: { labelKey: 'rejected', color: 'bg-red-500', textColor: 'text-red-500', icon: XCircle },
};

export default function ProposalsPage({ user, logout, lang = 'tr', setLang }) {
  const t = (key) => getTranslation(lang, key);
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Email Modal State
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchProposals();
  }, []);

  const fetchProposals = async () => {
    try {
      const response = await api.get('/proposals');
      setProposals(response.data);
    } catch (error) {
      toast.error(t('proposalsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedProposals = useMemo(() => {
    let result = [...proposals];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.title?.toLowerCase().includes(term) ||
        p.customer_name?.toLowerCase().includes(term) ||
        p.customer_email?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(p => (p.status || 'pending') === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'amount_desc':
          return (b.grand_total || 0) - (a.grand_total || 0);
        case 'amount_asc':
          return (a.grand_total || 0) - (b.grand_total || 0);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });

    return result;
  }, [proposals, searchTerm, statusFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedProposals.length / itemsPerPage);
  const paginatedProposals = filteredAndSortedProposals.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDeleteProposal'))) return;
    try {
      await api.delete(`/proposals/${id}`);
      toast.success(t('proposalDeleted'));
      fetchProposals();
    } catch (error) {
      toast.error(t('deleteFailed'));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/proposals/${id}`, { status: newStatus });
      toast.success(t('statusUpdated'));
      fetchProposals();
    } catch (error) {
      toast.error(t('updateFailed'));
    }
  };

  const handleDownloadPDF = async (proposal) => {
    try {
      toast.info(t('pdfGenerating'));
      const response = await api.get(`/proposals/${proposal.id}/pdf?lang=${lang}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${proposal.title || 'teklif'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF indirildi');
    } catch (error) {
      toast.error('PDF indirilemedi');
    }
  };

  const handlePrint = async (proposal) => {
    try {
      const response = await api.get(`/proposals/${proposal.id}/pdf?lang=${lang}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 1000);
        }, 500);
      };
    } catch (error) {
      toast.error(t('printError'));
    }
  };

  const formatCurrency = (amount) => formatMoney(amount || 0, lang);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Email Functions
  const openEmailModal = (proposal) => {
    setSelectedProposal(proposal);
    setEmailForm({
      recipient_email: proposal.customer_email || '',
      recipient_name: proposal.customer_name || '',
      subject: `Teklif: ${proposal.title || 'Teklif Belgesi'}`,
      message: ''
    });
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error(t('enterRecipientEmail'));
      return;
    }

    setSendingEmail(true);
    try {
      const response = await api.post(`/proposals/${selectedProposal.id}/send-email`, {
        proposal_id: selectedProposal.id,
        recipient_email: emailForm.recipient_email,
        recipient_name: emailForm.recipient_name,
        subject: emailForm.subject,
        message: emailForm.message,
        lang
      });
      
      toast.success(response.data.message);
      setEmailModalOpen(false);
      fetchProposals(); // Refresh to update status
    } catch (error) {
      toast.error(error.response?.data?.detail || t('emailSendFailed'));
    } finally {
      setSendingEmail(false);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const total = proposals.length;
    const totalAmount = proposals.reduce((sum, p) => sum + (p.grand_total || 0), 0);
    const approved = proposals.filter(p => p.status === 'approved').length;
    const pending = proposals.filter(p => p.status === 'pending' || !p.status).length;
    return { total, totalAmount, approved, pending };
  }, [proposals]);

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t('navProposals')}</h1>
                <p className="text-slate-500 text-xs sm:text-sm">{t('proposalsSubtitle')}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <LanguageSelector currentLang={lang} onLanguageChange={setLang} />
                <Button 
                  onClick={() => navigate('/proposal/new')}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs sm:text-sm"
                  data-testid="new-proposal-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('newProposal')}</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 sm:p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('totalProposals')}</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('statusApproved')}</p>
                    <p className="text-2xl font-bold text-emerald-600">{stats.approved}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('pending')}</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('totalAmount')}</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats.totalAmount)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-xl font-bold text-purple-600">₺</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="bg-white border-slate-200 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder={t('searchProposalPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder={t('statusFilterLabel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all')}</SelectItem>
                    <SelectItem value="draft">{t('draft')}</SelectItem>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="sent">{t('sent')}</SelectItem>
                    <SelectItem value="approved">{t('statusApproved')}</SelectItem>
                    <SelectItem value="rejected">{t('rejected')}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]" data-testid="sort-select">
                    <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder={t('sortLabel')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date_desc">{t('sortNewest')}</SelectItem>
                    <SelectItem value="date_asc">{t('sortOldest')}</SelectItem>
                    <SelectItem value="amount_desc">{t('sortAmountDesc')}</SelectItem>
                    <SelectItem value="amount_asc">{t('sortAmountAsc')}</SelectItem>
                    <SelectItem value="title">{t('sortTitleAZ')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Proposals Table */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="border-b border-slate-200">
              <CardTitle className="text-lg font-semibold">
                {t('proposalList')}
                <span className="text-sm font-normal text-slate-500 ml-2">
                  ({filteredAndSortedProposals.length} {t('proposalWord')})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : paginatedProposals.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">{t('noProposalsFound')}</p>
                  <Button
                    onClick={() => navigate('/proposal/new')}
                    className="mt-4 bg-amber-500 hover:bg-amber-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('createFirstProposalBtn')}
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {t('thProposal')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {t('thCustomer')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {t('dateLabel')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {t('statusLabel')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {t('amountLabel')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {t('actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {paginatedProposals.map((proposal) => {
                        const status = statusConfig[proposal.status || 'pending'];
                        const StatusIcon = status.icon;
                        return (
                          <tr 
                            key={proposal.id} 
                            className="hover:bg-slate-50 transition-colors"
                            data-testid={`proposal-row-${proposal.id}`}
                          >
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                  <FileText className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{proposal.title || t('untitledProposal')}</p>
                                  <p className="text-xs text-slate-500">#{proposal.id?.slice(0, 8)}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-slate-900">{proposal.customer_name || '-'}</p>
                              <p className="text-xs text-slate-500">{proposal.customer_email || ''}</p>
                            </td>
                            <td className="px-4 py-4 text-slate-600">
                              {formatDate(proposal.created_at)}
                            </td>
                            <td className="px-4 py-4">
                              <Select
                                value={proposal.status || 'pending'}
                                onValueChange={(value) => handleStatusChange(proposal.id, value)}
                              >
                                <SelectTrigger className="w-[140px] h-8">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${status.color}`} />
                                    <span className="text-sm">{t(status.labelKey)}</span>
                                  </div>
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.entries(statusConfig).map(([key, config]) => (
                                    <SelectItem key={key} value={key}>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${config.color}`} />
                                        {t(config.labelKey)}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-4 py-4 text-right font-medium text-slate-900">
                              {formatCurrency(proposal.grand_total)}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/proposal/${proposal.id}`)}
                                  className="h-8 w-8 p-0"
                                  title={t('edit')}
                                >
                                  <Edit className="w-4 h-4 text-slate-600" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEmailModal(proposal)}
                                  className="h-8 w-8 p-0"
                                  title={t('sendEmailTitle')}
                                  data-testid={`email-btn-${proposal.id}`}
                                >
                                  <Mail className="w-4 h-4 text-emerald-600" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDownloadPDF(proposal)}
                                  className="h-8 w-8 p-0"
                                  title={t('downloadPdf')}
                                >
                                  <Download className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handlePrint(proposal)}
                                  className="h-8 w-8 p-0"
                                  title={t('print')}
                                >
                                  <Printer className="w-4 h-4 text-purple-600" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDelete(proposal.id)}
                                  className="h-8 w-8 p-0"
                                  title={t('delete')}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                  <p className="text-sm text-slate-500">
                    {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredAndSortedProposals.length)} / {filteredAndSortedProposals.length} teklif
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <Button
                        key={page}
                        size="sm"
                        variant={currentPage === page ? 'default' : 'outline'}
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? 'bg-blue-600' : ''}
                      >
                        {page}
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              {t('sendProposal')}
            </DialogTitle>
            <DialogDescription>
              {selectedProposal?.title || t('subjectProposalPrefix')} — {t('sendByEmailDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient_email">{t('recipientEmail')} *</Label>
              <Input
                id="recipient_email"
                type="email"
                placeholder="ornek@email.com"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                data-testid="email-recipient-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipient_name">{t('recipientName')}</Label>
              <Input
                id="recipient_name"
                placeholder={t('customerNamePlaceholder')}
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">{t('subjectLabel')}</Label>
              <Input
                id="subject"
                placeholder={t('subjectLabel')}
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">{t('messageOptional')}</Label>
              <Textarea
                id="message"
                placeholder={t('messagePlaceholder')}
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                rows={4}
              />
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-800">
                <strong>{t('senderLabel')}:</strong> info@nktteklif.com
              </p>
              {(() => {
                try {
                  const s = JSON.parse(localStorage.getItem('nkt_company_settings') || '{}');
                  const rt = (s.sender_email || '').trim();
                  return rt ? (
                    <p className="text-xs text-emerald-700 mt-1">
                      ↩︎ {t('replyToLabel') || 'Yanıtlar buraya gelir'}: <strong>{rt}</strong>
                    </p>
                  ) : null;
                } catch { return null; }
              })()}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(false)}
              disabled={sendingEmail}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.recipient_email}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('sending')}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('sendBtn')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
